import React, { useEffect, useState } from 'react';

import { Autocomplete } from '@material-ui/lab';
import { Box, TextField, withStyles } from '@material-ui/core';

import BuzzLabel from '../BuzzLabel/BuzzLabel';

const CustomTextField = withStyles((theme) => ({
  root: {
    '& .MuiAutocomplete-inputRoot[class*="MuiInput-root"] .MuiAutocomplete-input:first-child':
      {
        padding: '10px',
      },

    '& .MuiOutlinedInput-root': {
      minHeight: 44,
      maxHeight: 52,

      '& fieldset': {
        borderColor: theme.palette.buzzBorder,
      },
      '&:hover fieldset': {
        borderColor: theme.palette.buzzBreadCrumb,
      },
    },
    '& .MuiOutlinedInput-root.Mui-disabled': {
      '& fieldset': {
        borderColor: theme.palette.buzzBorder,
      },
      '&:hover fieldset': {
        borderColor: theme.palette.buzzBorder,
      },
    },
    '& .MuiInputBase-root.Mui-disabled': {
      background: theme.palette.buzzLiteGray,
    },
    '& .MuiInputBase-input.Mui-disabled': {
      color: theme.palette.buzzDisableInputColor,
    },

    '& ::before': { display: 'none' },
    '& ::before:focus': { display: 'none' },
    // '& ': { padding: '10px' },
  },
}))(TextField);

function BuzzAutoCompleteSingleSelectInput({
  id,
  label,
  placeholder,
  options,
  value,
  onChange,
  required = false,
  bgcolor = 'white',
  disabled = false,
  size,
}) {
  const [selectedValue, setSelectedValue] = useState(null);

  // The `.MuiOutlinedInput-root` min/max-height above didn't actually shrink anything when
  // set as a JSS dynamic (prop-based) value — inline style is used instead here since it
  // always wins on specificity, no matter what else is fighting for this rule. 40px/16px
  // matches the school selector on the Widget Setup page pixel-for-pixel (verified via
  // devtools computed styles), which is the reference this is meant to match.
  // `minHeight`/`maxHeight` must be overridden explicitly, not just `height` — the class
  // above sets `minHeight: 44`, and CSS's used height is the *greater* of `height` and
  // `min-height`, so a bare inline `height: 40` would still get clamped up to 44.
  const compactStyle =
    size === 'small'
      ? { height: 40, minHeight: 40, maxHeight: 40, fontSize: 16 }
      : undefined;

  // A content-based key, not the `options` array itself — callers pass a freshly mapped
  // array/objects on every render, so depending on the array reference directly would
  // re-run this effect (and call `setSelectedValue` with a new object) every render,
  // looping forever. This only changes when the actual set of option values changes.
  const optionValuesKey = options
    .map(({ value: optionValue }) => optionValue)
    .join(',');

  // Re-resolves once the matching `options` arrive, not just when `value` changes — on
  // first load after a reload, `value` can already be set (e.g. restored from
  // sessionStorage) before its options have finished fetching. Without `optionValuesKey`
  // here, that first pass resolves to nothing and never retries once the real options
  // arrive, leaving the input blank even though `value` — and everything that reads it —
  // is already correct.
  useEffect(() => {
    if (value === undefined) return;

    if (value === null) setSelectedValue(null);
    else {
      const selectedValues = options.filter(({ value: optionValue }) => {
        return optionValue === value;
      });

      setSelectedValue(selectedValues[0]);
    }
  }, [value, optionValuesKey]);

  return (
    <Autocomplete
      getOptionLabel={({ label }) => label}
      getOptionSelected={({ value }) => value}
      // disablePortal
      id={id}
      options={options}
      disabled={disabled}
      size={size}
      sx={{ width: 300 }}
      renderInput={(params) => (
        <>
          {' '}
          <BuzzLabel label={label} required={required} id={id} />
          <Box
            flex={1}
            bgcolor={bgcolor}
            borderRadius={4}
            border={1}
            borderColor={'#B9B9B9'}>
            <CustomTextField
              {...params}
              placeholder={placeholder}
              size={size}
              InputProps={{
                ...params.InputProps,
                style: {
                  ...(params.InputProps && params.InputProps.style),
                  ...compactStyle,
                },
              }}
            />
          </Box>
        </>
      )}
      onChange={(e, selectedOption) => {
        if (selectedOption) onChange(selectedOption.value);
        else onChange(null);
      }}
      value={selectedValue}
    />
  );
}

export default BuzzAutoCompleteSingleSelectInput;
